import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, User, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GoogleButton } from "@/components/GoogleButton";
import { LocationSelect } from "@/components/LocationSelect";
import { AuthShell, Divider } from "@/pages/Login";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function Register() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [accountType, setAccountType] = useState("bireysel");
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", city: "", district: "",
    company_name: "", tax_office: "", tax_number: "", website: "", company_description: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        name: form.name, email: form.email, password: form.password,
        phone: form.phone, city: form.city, district: form.district,
        account_type: accountType,
        company: accountType === "kurumsal" ? {
          company_name: form.company_name, tax_office: form.tax_office, tax_number: form.tax_number,
          website: form.website, company_description: form.company_description,
        } : null,
      };
      const { data } = await api.post("/auth/register", payload);
      setSession(data);
      toast.success("Hesabınız oluşturuldu!");
      navigate("/panel", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Hesap oluştur" subtitle="Ücretsiz üye ol, hemen ilan ver">
      <div className="mb-5 grid grid-cols-2 gap-3">
        <TypeCard active={accountType === "bireysel"} onClick={() => setAccountType("bireysel")}
          icon={User} title="Bireysel Hesap" desc="Kişisel alım ve satım" testid="account-type-bireysel" />
        <TypeCard active={accountType === "kurumsal"} onClick={() => setAccountType("kurumsal")}
          icon={Building2} title="Kurumsal Hesap" desc="Firma / işletme" testid="account-type-kurumsal" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="register-error">{error}</p>}
        <Field label="Ad Soyad" required value={form.name} onChange={set("name")} testid="register-name" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="E-posta" type="email" required value={form.email} onChange={set("email")} testid="register-email" />
          <Field label="Şifre" type="password" required value={form.password} onChange={set("password")} testid="register-password" placeholder="En az 6 karakter" />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Telefon" value={form.phone} onChange={set("phone")} testid="register-phone" />
          <LocationSelect city={form.city} district={form.district}
            onChange={(city, district) => setForm({ ...form, city, district })}
            cityTestId="register-city" districtTestId="register-district" />
        </div>

        {accountType === "kurumsal" && (
          <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-700">Firma Bilgileri</p>
            <Field label="Firma Adı" required value={form.company_name} onChange={set("company_name")} testid="register-company-name" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Vergi Dairesi" value={form.tax_office} onChange={set("tax_office")} testid="register-tax-office" />
              <Field label="Vergi No" value={form.tax_number} onChange={set("tax_number")} testid="register-tax-number" />
            </div>
            <Field label="Web Sitesi" value={form.website} onChange={set("website")} testid="register-website" placeholder="https://" />
            <div className="space-y-1.5">
              <Label>Firma Açıklaması</Label>
              <Textarea value={form.company_description} onChange={set("company_description")} data-testid="register-company-desc"
                className="focus-visible:ring-emerald-500" rows={2} />
            </div>
            <p className="text-xs text-slate-500">Kurumsal hesaplar yönetici onayından sonra "Onaylı Kurumsal Satıcı" rozeti kazanır.</p>
          </div>
        )}

        <Button type="submit" disabled={loading} data-testid="register-submit"
          className="w-full bg-emerald-600 font-semibold hover:bg-emerald-700">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Üye Ol
        </Button>
      </form>
      <Divider />
      <GoogleButton label="Google ile üye ol" />
      <p className="mt-6 text-center text-sm text-slate-600">
        Zaten üye misiniz? <Link to="/giris" className="font-semibold text-emerald-700 hover:underline">Giriş Yap</Link>
      </p>
    </AuthShell>
  );
}

function TypeCard({ active, onClick, icon: Icon, title, desc, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={cn("flex flex-col items-start rounded-xl border p-3 text-left transition-all",
        active ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600" : "border-slate-200 hover:border-slate-300")}>
      <Icon className={cn("mb-1.5 h-5 w-5", active ? "text-emerald-600" : "text-slate-400")} />
      <span className="text-sm font-semibold text-slate-800">{title}</span>
      <span className="text-xs text-slate-500">{desc}</span>
    </button>
  );
}

function Field({ label, required, type = "text", value, onChange, testid, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}{required && <span className="text-red-500"> *</span>}</Label>
      <Input type={type} required={required} value={value} onChange={onChange} data-testid={testid}
        placeholder={placeholder} className="focus-visible:ring-emerald-500" />
    </div>
  );
}
